import axios from "axios";
import { isTokenExpired } from "../../utils/tokenUtils";
 
let API_URL =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  validateStatus: function (status) {
    return status < 500; // Accept any status code less than 500
  }
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use((config) => {
  // Get token from sessionStorage directly to avoid circular dependencies
  const userData = sessionStorage.getItem("user");
  let token = null;
  
  if (userData) {
    try {
      const user = JSON.parse(userData);
      token = user.token;
    } catch (error) {
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("refreshToken");
    }
  }
  
  if (token && !isTokenExpired(token)) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (token && isTokenExpired(token)) {
    // Token is expired, clear it from sessionStorage
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("refreshToken");
    // Note: We don't redirect here to avoid conflicts with routing
  }

  // Let the browser set multipart boundary for file uploads
  if (config.data instanceof FormData && config.headers) {
    if (typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
      config.headers.delete("content-type");
    } else if (typeof config.headers.set === "function") {
      config.headers.set("Content-Type", false);
    } else {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
      config.headers["Content-Type"] = undefined;
    }
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor to handle 401 errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    // Skip processing for blob responses (file downloads)
    if (response.config?.responseType === 'blob') {
      return response;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Skip processing for blob responses (file downloads) - let them fail naturally
    if (originalRequest?.responseType === 'blob') {
      return Promise.reject(error);
    }
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Check if keepMeLogin is enabled
        const userData = sessionStorage.getItem("user");
        const keepMeLoginStored = localStorage.getItem("keepMeLogin");
        let keepMeLogin = false;
        
        if (userData) {
          try {
            const user = JSON.parse(userData);
            keepMeLogin = user.keepMeLogin || false;
          } catch (error) {
            // Fallback to stored keepMeLogin preference
          }
        }
        
        // Also check the separate keepMeLogin storage
        if (!keepMeLogin && keepMeLoginStored) {
          try {
            keepMeLogin = JSON.parse(keepMeLoginStored);
          } catch (error) {
            // Invalid keepMeLogin data
          }
        }
        
        // Only attempt token refresh if keepMeLogin is enabled
        if (keepMeLogin) {
          const refreshToken = sessionStorage.getItem("refreshToken");
          if (refreshToken) {
            try {
              const refreshResponse = await axios.post(`${API_URL}/api/Auth/RefreshToken`, {
                refreshToken: refreshToken
              });
              
              if (refreshResponse.data.status && refreshResponse.data.data.token) {
                // Update stored tokens in sessionStorage
                const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
                userData.token = refreshResponse.data.data.token;
                userData.keepMeLogin = keepMeLogin; // Ensure keepMeLogin is preserved
                if (refreshResponse.data.data.refreshToken) {
                  userData.refreshToken = refreshResponse.data.data.refreshToken;
                  sessionStorage.setItem('refreshToken', refreshResponse.data.data.refreshToken);
                }
                sessionStorage.setItem('user', JSON.stringify(userData));
                
                // Dispatch Redux action to update store
                window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
                  detail: { 
                    token: refreshResponse.data.data.token,
                    refreshToken: refreshResponse.data.data.refreshToken || refreshToken,
                    keepMeLogin: keepMeLogin
                  } 
                }));
                
                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.data.token}`;
                return axiosInstance(originalRequest);
              } else {
                console.warn('Token refresh failed: Invalid response', refreshResponse.data);
              }
            } catch (refreshError) {
              console.warn('Token refresh request failed:', refreshError.response?.data?.message || refreshError.message);
              throw refreshError; // Re-throw to be caught by outer catch
            }
          } else {
            console.warn('No refresh token available for token refresh');
          }
        } else {
          console.info('keepMeLogin is disabled, not attempting token refresh');
        }
      } catch (refreshError) {
        console.error('Token refresh process failed:', refreshError);
      }
      
      // If refresh failed or keepMeLogin is disabled, clear tokens
      console.info('Clearing tokens and forcing logout due to 401 error');
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("refreshToken");
      localStorage.removeItem("keepMeLogin");
      
      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('forceLogout'));
      
      return Promise.reject(error);
    }
    
    // Handle other errors
    if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;
